import React, { useContext, useEffect, useRef, useState } from 'react';

import throttle from 'lodash.throttle';
import { Box, Heading, Link } from 'lucid-components-react';
import LazyLoad from 'react-lazyload';
import {
  Drupal_ParagraphMarketoForm,
  Drupal_ParagraphStickySlider
} from 'generated/drupal-graphql';
import { getProcessedValue, LucidContainer, isBrowser } from '@lucid/gatsby-theme-lucid';

import LayoutContext from 'contexts/layoutContext';
import useMarketoLink from 'hooks/use-marketo-link';
import { LucidTheme } from 'types/types';
import { mqMax, mqMin } from 'theme/base';

interface StickySliderProps {
  sections: Drupal_ParagraphStickySlider[];
  mktoFormEntity: Drupal_ParagraphMarketoForm | null;
  buttonText: string;
}

const StickySlider = ({
  sections,
  mktoFormEntity,
  buttonText
}: StickySliderProps) => {
  const slideHeight = 900;
  const sliderRef = useRef<HTMLDivElement>(null);

  const positionStickySupport = () => {
    let support = true;
    if (isBrowser()) {
      const el = document.createElement('a');
      el.id = 'sticky-slider-link';
      const mStyle = el.style;
      mStyle.cssText =
        'position:sticky;position:-webkit-sticky;position:-ms-sticky;';

      if (!mStyle.position.includes('sticky')) {
        support = false;
      }
    }
    return support;
  };

  const [slideIndex, setSlideIndex] = useState(0);
  const stickyIsSupported = positionStickySupport();
  const onScroll = throttle(() => {
    const sliderElement = sliderRef.current;
    if (sliderElement) {
      const sliderPosition = sliderElement.getBoundingClientRect();
      if (sliderPosition.top < 0) {
        const currentSlide = Math.round(
          Math.abs(sliderPosition.top / slideHeight)
        );
        if (currentSlide <= sections.length - 1) {
          setSlideIndex(currentSlide);
        }
      } else {
        setSlideIndex(0);
      }
    }
  }, 250);

  useEffect(() => {
    const sliderElement = sliderRef.current;
    if (stickyIsSupported && sliderElement) {
      sliderElement.style.minHeight = `${sections.length * slideHeight}px`;
      window.addEventListener('scroll', onScroll);
    }
  }, [onScroll, sections.length, stickyIsSupported]);

  const scrollToSlide = (i: number) => {
    if (window.innerWidth > 1219) {
      const sliderElement = sliderRef.current;
      if (sliderElement) {
        const parent = sliderElement.parentElement;
        if (parent) {
          const scrollDistance = slideHeight * i + parent.offsetTop;
          window.scrollTo(0, scrollDistance);
        }
      }
    }
  };

  const { language } = useContext(LayoutContext);
  const getMarketoLink = useMarketoLink(language);

  const { url, copy } = getMarketoLink(mktoFormEntity?.fieldMarketoId);

  return (
    <LucidContainer
      as="section"
      css={(theme: LucidTheme) => ({
        position: 'relative',
        paddingTop: `calc(${theme.space[10]}px + ${theme.space[8]}px)`,
        paddingBottom: `calc(${theme.space[10]}px + ${theme.space[8]}px)`,
        [mqMax.computer]: {
          paddingTop: theme.space[9],
          paddingBottom: theme.space[9]
        }
      })}
    >
      <Box
        ref={sliderRef}
        css={{
          position: 'relative'
        }}
      >
        <Box
          css={(theme: LucidTheme) => [
            {
              bottom: 0,
              fontSize: 0,
              position: 'absolute',
              right: 0,
              top: -theme.space[8],
              width: '66%'
            },
            stickyIsSupported
              ? {
                [mqMax.tablet]: {
                  display: 'none'
                }
              }
              : {
                display: 'none !important'
              }
          ]}
        >
          <Box
            css={(theme: LucidTheme) => ({
              marginLeft: 'auto',
              overflow: 'visible',
              position: 'sticky',
              top: 'calc(6rem + 6vh)',
              zIndex: theme.zIndices[1]
            })}
          >
            {sections.map(
              (
                {
                  fieldHeader,
                  fieldHideImageShadow,
                  fieldDoNotAddBrow,
                  fieldImage
                },
                i
              ) => {
                return (
                  <Box
                    key={fieldHeader}
                    css={(theme: LucidTheme) => [{
                      backgroundColor: theme.colors.white,
                      border: `1px solid ${theme.colors.coolGrey[5]}`,
                      borderRadius: theme.borderRadius,
                      zIndex: theme.zIndices[1],
                      transition: 'opacity 1s ease',
                      height: 0,
                      opacity: 0,
                      position: 'absolute',
                    },
                    slideIndex === i && {
                      height: 'auto',
                      opacity: 1,
                      position: 'relative',
                    },
                    !fieldHideImageShadow && {
                      boxShadow: theme.shadows[20]
                    }
                    ]}
                  >
                    {!fieldDoNotAddBrow && (
                      <img
                        alt="browserbar"
                        src="https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/consideration/basic-images/browser-bar@2x.png"
                      />
                    )}
                    <LazyLoad offset={10}>
                      <img alt={fieldImage!} src={fieldImage!} />
                    </LazyLoad>
                  </Box>
                );
              }
            )}
            {mktoFormEntity && (
              <Box
                css={(theme: LucidTheme) => ({
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  width: '100%',
                  marginTop: theme.space[8]
                })}
              >
                <Box
                  css={{
                    margin: 0,
                    flex: '0 1 41.6%',
                    maxWidth: '41.6%',
                  }}
                >
                  <Link asButton block primary href={url}>
                    {buttonText || copy}
                  </Link>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <Box
          css={{
            position: 'sticky',
            top: 'calc(6rem + 6vh)',
          }}
        >
          {sections.map(
            ({ fieldHeader, fieldImage, fieldBody, fieldCta }, i) => (
              <Box key={fieldHeader}>
                <Box
                  css={[{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    width: '100%',
                  }, stickyIsSupported ?
                    {
                      [mqMax.tablet]: {
                        display: 'none'
                      }
                    }
                    : { display: 'none !important' }]}
                >
                  <Box
                    css={(theme: LucidTheme) => ({
                      borderTop: `1px solid ${theme.colors.coolGrey[5]}`,
                      maxWidth: '41.6%',
                      margin: 0,
                      flex: '0 1 41.6%'
                    })}
                  />
                </Box>
                <Box
                  css={(theme: LucidTheme) => ({
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    width: '100%',
                    borderTop: `1px solid ${theme.colors.coolGrey[5]}`,
                  })}
                >
                  <Box
                    css={(theme: LucidTheme) => [{
                      margin: 0,
                      flex: '0 1',
                      boxShadow: theme.shadows[20],
                      border: 0,
                      paddingBottom: theme.space[0],
                      [mqMax.tabletSmall]: {
                        flexBasis: '100%',
                        maxWidth: '100%',
                      }
                    },
                    stickyIsSupported && {
                      [mqMin.computer]: {
                        display: 'none !important'
                      }
                    }
                    ]}

                  >
                    <img
                      alt="browserbar"
                      src="https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/consideration/basic-images/browser-bar@2x.png"
                    />
                    <img
                      alt={fieldHeader!}
                      src={fieldImage!}
                    />
                  </Box>
                  <Box
                    as="section"
                    css={(theme: LucidTheme) => [{
                      margin: 0,
                      flex: '0 1 25%',
                      maxWidth: '25%',
                      [mqMax.computer]: {
                        padding: `${theme.space[5]}px ${theme.space[0]}`,
                        flexBasis: '75%',
                        maxWidth: '75%',
                      },
                      [mqMax.tabletSmall]: {
                        flexBasis: '100%',
                        maxWidth: '100%',
                      }
                    },
                    !stickyIsSupported && {
                      height: 'auto',
                      flexBasis: '75%',
                      maxWidth: '75%',
                    }
                  ]}
                  >
                    <button
                      css={(theme: LucidTheme) => ({
                        background: 'none',
                        border: 0,
                        padding: theme.space[0]
                      })}
                      onClick={() => scrollToSlide(i)}
                    >
                      <Heading
                        as="h3"
                        css={(theme: LucidTheme) => ({
                          cursor: 'pointer',
                          margin: theme.space[0],
                          padding: `${theme.space[3]}px ${theme.space[0]}`,
                          textAlign: 'left',
                          transition: 'padding 0.25s ease',
                          [mqMax.computer]: {
                            cursor: 'text',
                            padding: `${theme.space[5]}px ${theme.space[0]}`
                          }
                        })}
                        variant="subheadXXS"
                      >
                        {fieldHeader}
                      </Heading>
                    </button>
                    {fieldBody && (
                      <Box
                        css={(theme: LucidTheme) => [{
                          height: 0,
                          overflow: 'hidden',
                          paddingBottom: theme.space[0],
                          [mqMax.computer]: {
                            height: 'initial',
                            marginBottom: theme.space[5]
                          }
                        },
                        stickyIsSupported && slideIndex === i && {
                          height: 'auto'
                        },
                        ]}
                      >
                        <Box
                          dangerouslySetInnerHTML={{
                            __html: getProcessedValue(fieldBody)
                          }}
                        />
                        {fieldCta && fieldCta.url && (
                          <Link
                            css={(theme: LucidTheme) => ({
                              color: theme.colors.orange[45]
                            })}
                            href={fieldCta.url.path!}
                          >
                            {fieldCta.title}
                          </Link>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            )
          )}
        </Box>
      </Box>
    </LucidContainer>
  );
};

export default StickySlider;
